// frontend/src/pages/reports/useAlwaysInclude.js
//
// State for the "always include these items" setting shared by the Stock
// Report, Stock Movements, Inventory Value on Date, BOM Usage and Raw
// Material Usage reports:
//
//   - hand-picked items (chips) — sent with every run as
//     `always_include_codes`; "Save" persists them for everyone
//   - uploaded code sheets — each worksheet is saved server-side with a
//     tick (`enabled`); ticked sheets always contribute, the tick itself is
//     the saved state so there is nothing to "save" for sheets.
//
// Every report has its OWN list: Reports.js holds one hook instance per
// report key so a report's card and dialog show the same thing, and no
// report sees another's picks or sheets.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { manufacturingAPI } from '../../services/api';

const codesOf = (items) => items.map((i) => i.item_code).join(',');

const rowsFrom = (data) => [
  ...(data?.items || []),
  ...(data?.unknown || []).map((c) => ({ item_code: c, name: '', unknown: true })),
];

export default function useAlwaysInclude(reportKey) {
  const [items, setItems] = useState([]);
  const [savedCodes, setSavedCodes] = useState('');
  const [saving, setSaving] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [sheetsBusy, setSheetsBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const codes = codesOf(items);
  const dirty = codes !== savedCodes;

  const load = useCallback(async () => {
    try {
      const [codesRes, sheetsRes] = await Promise.all([
        manufacturingAPI.reports.getAlwaysIncludeCodes(reportKey),
        manufacturingAPI.reports.listCodeSheets(reportKey),
      ]);
      const rows = rowsFrom(codesRes.data);
      setItems(rows);
      setSavedCodes(codesOf(rows));
      setSheets(sheetsRes.data?.sheets || []);
    } catch (error) {
      console.error('Failed to load always-include settings:', error);
    }
  }, [reportKey]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const res = await manufacturingAPI.reports.saveAlwaysIncludeCodes(reportKey, items.map((i) => i.item_code));
      const rows = rowsFrom(res.data);
      setItems(rows);
      setSavedCodes(codesOf(rows));
      const unknown = res.data?.unknown || [];
      if (unknown.length > 0) {
        toast.warning(`Saved, but ${unknown.length} code${unknown.length === 1 ? '' : 's'} not found: ${unknown.join(', ')}`);
      } else {
        toast.success(`Always-include items saved (${rows.length})`);
      }
    } catch (error) {
      console.error('Failed to save always-include codes:', error);
      toast.error(error.response?.data?.error || 'Failed to save items');
    } finally {
      setSaving(false);
    }
  }, [reportKey, items]);

  const uploadSheet = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('report', reportKey);
      fd.append('file', file);
      const res = await manufacturingAPI.reports.uploadCodeSheet(fd);
      setSheets(res.data?.sheets || []);
      const created = res.data?.created || [];
      const total = created.reduce((t, s) => t + (s.code_count || 0), 0);
      const skipped = res.data?.skipped_empty || [];
      toast.success(
        `${res.data?.replaced ? 'Replaced' : 'Saved'} ${created.length} sheet${created.length === 1 ? '' : 's'} `
        + `(${total} code${total === 1 ? '' : 's'}) from ${file.name}`
        + (skipped.length ? ` — skipped empty: ${skipped.join(', ')}` : ''),
      );
    } catch (error) {
      console.error('Failed to upload code sheet:', error);
      toast.error(error.response?.data?.error || 'Failed to read that file');
    } finally {
      setUploading(false);
    }
  }, [reportKey]);

  const downloadTemplate = useCallback(async () => {
    try {
      const res = await manufacturingAPI.reports.downloadCodeSheetTemplate();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Always_Include_Codes_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download code sheet template:', error);
      toast.error('Failed to download template');
    }
  }, []);

  const setSheetEnabled = useCallback(async (id, enabled) => {
    // Optimistic tick; the server reply is authoritative.
    setSheets((prev) => prev.map((s) => (s.id === id ? { ...s, enabled } : s)));
    setSheetsBusy(true);
    try {
      const res = await manufacturingAPI.reports.updateCodeSheet(id, { enabled });
      setSheets(res.data?.sheets || []);
    } catch (error) {
      console.error('Failed to update code sheet:', error);
      toast.error('Failed to update sheet');
      load();
    } finally {
      setSheetsBusy(false);
    }
  }, [load]);

  const setAllSheets = useCallback(async (enabled) => {
    setSheetsBusy(true);
    try {
      const res = await manufacturingAPI.reports.bulkCodeSheets({ report: reportKey, enabled });
      setSheets(res.data?.sheets || []);
    } catch (error) {
      console.error('Failed to update code sheets:', error);
      toast.error('Failed to update sheets');
    } finally {
      setSheetsBusy(false);
    }
  }, [reportKey]);

  const renameSheet = useCallback(async (id, name) => {
    try {
      const res = await manufacturingAPI.reports.updateCodeSheet(id, { name });
      setSheets(res.data?.sheets || []);
    } catch (error) {
      console.error('Failed to rename code sheet:', error);
      toast.error(error.response?.data?.error || 'Failed to rename sheet');
    }
  }, []);

  const deleteSheet = useCallback(async (id) => {
    setSheetsBusy(true);
    try {
      const res = await manufacturingAPI.reports.deleteCodeSheet(id);
      setSheets(res.data?.sheets || []);
      toast.success('Sheet removed');
    } catch (error) {
      console.error('Failed to delete code sheet:', error);
      toast.error('Failed to remove sheet');
    } finally {
      setSheetsBusy(false);
    }
  }, []);

  // What the reports will actually include right now: picked items plus
  // every code on a ticked sheet, de-duplicated.
  const effective = useMemo(() => {
    const set = new Set(items.map((i) => i.item_code.toUpperCase()));
    const picked = set.size;
    sheets.filter((s) => s.enabled).forEach((s) => (s.codes || []).forEach((c) => set.add(c.toUpperCase())));
    return { total: set.size, picked, fromSheets: set.size - picked };
  }, [items, sheets]);

  return {
    reportKey,
    items, setItems, codes, dirty, saving, save,
    sheets, sheetsBusy, uploading, uploadSheet, downloadTemplate, setSheetEnabled, setAllSheets,
    renameSheet, deleteSheet, effective, reload: load,
  };
}
